import { useCallback, useRef, useState } from "react";

/**
 * Guards an async action against being fired twice by a fast double-
 * click (or any other near-simultaneous re-trigger).
 *
 * `disabled={busy}` alone isn't enough: React only reflects a state
 * update in the DOM on the NEXT render, so two click events dispatched
 * within the same tick (a fast double-click, or a stray duplicate
 * touch event on mobile) can both invoke the handler before that
 * render — and thus the real `disabled` attribute — ever lands. A
 * plain ref check runs synchronously as the very first line of the
 * handler, before any state update or awaited call, so the second
 * invocation always sees the guard already set, no matter how fast it
 * arrives.
 *
 * Usage:
 *   const [busy, run] = useSubmitGuard();
 *   <button disabled={busy} onClick={() => run(async () => { ... })}>
 */
export function useSubmitGuard() {
    const inFlight = useRef(false);
    const [busy, setBusy] = useState(false);

    const run = useCallback(async (fn) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        try {
            await fn();
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    }, []);

    return [busy, run];
}

/**
 * Same guard, but keyed — for a list where each row needs its own
 * independent in-flight lock instead of one shared flag disabling
 * every row's button while any single one is in flight.
 *
 * Usage:
 *   const [busyKey, runKeyed] = useKeyedSubmitGuard();
 *   const key = `${order.id}:${action.slug}`;
 *   <button disabled={busyKey === key} onClick={() => runKeyed(key, async () => { ... })}>
 */
export function useKeyedSubmitGuard() {
    const inFlight = useRef(new Set());
    const [busyKey, setBusyKey] = useState(null);

    const run = useCallback(async (key, fn) => {
        if (inFlight.current.has(key)) return;
        inFlight.current.add(key);
        setBusyKey(key);
        try {
            await fn();
        } finally {
            inFlight.current.delete(key);
            setBusyKey((k) => (k === key ? null : k));
        }
    }, []);

    return [busyKey, run];
}
